/**
 * Settings Tour Configuration
 *
 * Detailed tours for Settings page sections. Each section has its own tour
 * that explains the main features and functionality.
 */

import { filterExistingSteps } from './tourUtils.js';

const SETTINGS_NAV_SELECTOR =
  '.settings-sidebar__item.active, .settings-sidebar[data-tour="settings-navigation"], .settings-mobile-select-wrapper[data-tour="settings-navigation"]';

export const getSettingsProfileTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.profile.overview', {
      ns: 'common',
      defaultValue: 'Profile Settings - Manage your personal information, address, and preferences. Update your details and customize your account settings here.'
    }),
    position: 'bottom'
  },
  {
    selector: '.settings-section[data-tour="settings-profile-personal"]',
    content: t('tour.settings.profile.personal', {
      ns: 'common',
      defaultValue: 'Your personal information including name and phone number. These fields are read-only and cannot be changed here.'
    }),
    position: 'bottom'
  },
  {
    selector: '.settings-section[data-tour="settings-profile-address"]',
    content: t('tour.settings.profile.address', {
      ns: 'common',
      defaultValue: 'Update your address information including country, city, area, and digital address (Ghana Post GPS).'
    }),
    position: 'bottom'
  },
  {
    selector: '.settings-section[data-tour="settings-profile-preferences"]',
    content: t('tour.settings.profile.preferences', {
      ns: 'common',
      defaultValue: 'Customize your preferences: currency, language, notification method, theme, and timezone. Changes are saved locally and synced with your account.'
    }),
    position: 'bottom'
  }
]);

/**
 * Payment Methods / Settlement Methods Tour
 */
export const getSettingsPaymentMethodsTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.paymentMethods.overview', {
      ns: 'common',
      defaultValue: 'Settlement Methods - Manage your mobile money accounts, bank accounts, and settlement preferences. Configure how you receive payments and payouts.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-payment-tabs"]',
    content: t('tour.settings.paymentMethods.tabs', {
      ns: 'common',
      defaultValue: 'Switch between Mobile Money, Bank Accounts, and Settlement Configuration tabs to manage different payment methods.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-payment-mobile"]',
    content: t('tour.settings.paymentMethods.mobile', {
      ns: 'common',
      defaultValue: 'Add or manage your mobile money wallet for receiving payouts. You can set one mobile money account as default.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-payment-bank"]',
    content: t('tour.settings.paymentMethods.bank', {
      ns: 'common',
      defaultValue: 'Add or manage bank accounts for receiving settlements. Configure account details and set a default bank account.'
    }),
    position: 'bottom'
  },
  {
    selector: '.settlement-config-section[data-tour="settings-payment-settlement"]',
    content: t('tour.settings.paymentMethods.settlement', {
      ns: 'common',
      defaultValue: 'Configure your settlement preferences. Choose automatic or manual settlement mode and select default payment methods for payouts.'
    }),
    position: 'bottom'
  }
]);

/**
 * Security Settings Tour
 */
export const getSettingsSecurityTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.security.overview', {
      ns: 'common',
      defaultValue: 'Security Settings - Manage your account security including password, security PIN, and two-factor authentication. Keep your account safe and secure.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-security-summary"]',
    content: t('tour.settings.security.summary', {
      ns: 'common',
      defaultValue: 'View your account security status at a glance. See if security PIN and two-factor authentication are enabled.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-security-pin"]',
    content: t('tour.settings.security.pin', {
      ns: 'common',
      defaultValue: 'Set or update your 4-digit security PIN. This PIN is required to authorize transactions and secure your account.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-security-2fa"]',
    content: t('tour.settings.security.twoFactor', {
      ns: 'common',
      defaultValue: 'Enable two-factor authentication for extra security. Scan the QR code with an authenticator app to get verification codes.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-security-password"]',
    content: t('tour.settings.security.password', {
      ns: 'common',
      defaultValue: 'Change your account password. You will be logged out after changing your password and will need to log in again.'
    }),
    position: 'top'
  }
]);

/**
 * Verification Settings Tour
 * Works for both Individual and Business verification pages
 * Highlights only the verification level cards showing requirements and transaction limits
 */
export const getSettingsVerificationTourSteps = (t) => filterExistingSteps([
  {
    selector: '[data-tour="settings-verification-header"] h3, [data-tour="settings-verification-header"], .invoices-card[data-tour="settings-verification"] .card-head, [data-tour="settings-verification"], ' + SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.verification.overview', {
      ns: 'common',
      defaultValue: 'Verification Settings - Complete your KYC verification to increase your transaction limits. Each level shows what is required and the transaction limit you can unlock.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-header"]',
    content: t('tour.settings.verification.header', {
      ns: 'common',
      defaultValue: 'Account Verification - This page shows your verification status across multiple levels. Each level card displays the requirements and transaction limit you can unlock.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-email"]',
    content: t('tour.settings.verification.email', {
      ns: 'common',
      defaultValue: 'Verify your email address to unlock Level 1 verification.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-phone"]',
    content: t('tour.settings.verification.phone', {
      ns: 'common',
      defaultValue: 'Verify your phone number to complete Level 1 requirements.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-documents"]',
    content: t('tour.settings.verification.documents', {
      ns: 'common',
      defaultValue: 'Upload required documents (e.g., Ghana Card, proof of address) to progress to higher verification levels.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-level-0"]',
    content: t('tour.settings.verification.level0', {
      ns: 'common',
      defaultValue: 'Level 0 - Basic Verification: Automatically completed when you create your account. Includes email and phone number registration. Transaction limit: GHS 1,000.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-level-1"]',
    content: t('tour.settings.verification.level1', {
      ns: 'common',
      defaultValue: 'Level 1 - Email & Phone Verified: Requires email and phone number verification. Transaction limit: GHS 5,000. Complete the verification steps shown in this card to unlock this level.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-level-2"]',
    content: t('tour.settings.verification.level2', {
      ns: 'common',
      defaultValue: 'Level 2 - ID Verification: Requires uploading your Ghana Card (front and back). For business users, additional business documents are required. Transaction limit: GHS 20,000.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-level-3"]',
    content: t('tour.settings.verification.level3', {
      ns: 'common',
      defaultValue: 'Level 3 - Full KYC: Requires all document uploads including proof of address and additional verification documents. Transaction limit: GHS 100,000 (or unlimited for business users).'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-verification-guidelines"]',
    content: t('tour.settings.verification.guidelines', {
      ns: 'common',
      defaultValue: 'Review verification guidelines and tips to ensure your documents are approved quickly.'
    }),
    position: 'bottom'
  }
]);

/**
 * Business Settings Tour (Business-specific)
 */
export const getSettingsBusinessTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.business.overview', {
      ns: 'common',
      defaultValue: 'Business Settings - Manage your business information, owners, and bank accounts. Update your business details and registration information.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-business-info"]',
    content: t('tour.settings.business.info', {
      ns: 'common',
      defaultValue: 'Update your business information including trading name, description, staff size, expected revenue, and TIN number.'
    }),
    position: 'bottom'
  },
  {
    selector: '.invoices-card[data-tour="settings-business-owners"]',
    content: t('tour.settings.business.owners', {
      ns: 'common',
      defaultValue: 'Manage business owners and their information. Add owner details including ownership percentage and identification documents.'
    }),
    position: 'bottom'
  },
  {
    selector: '.invoices-card[data-tour="settings-business-bank"]',
    content: t('tour.settings.business.bank', {
      ns: 'common',
      defaultValue: 'Configure business bank accounts for receiving payments. Add multiple bank accounts and set a default account.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-business-edit-button"]',
    content: t('tour.settings.business.edit', {
      ns: 'common',
      defaultValue: 'Click here to edit your business information. You can update your trading name, description, and registration details.'
    }),
    position: 'bottom'
  },
  {
    selector: '[data-tour="settings-business-danger-zone"]',
    content: t('tour.settings.business.dangerZone', {
      ns: 'common',
      defaultValue: 'Danger Zone - This section contains high-risk actions like deleting your business account. Be careful as these actions are irreversible.'
    }),
    position: 'top'
  }
]);

/**
 * API Management Tour (Business-specific)
 */
export const getSettingsAPITourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.api.overview', {
      ns: 'common',
      defaultValue: 'API Management - Generate and manage API keys for integrating with iSmartPay. Use these keys to connect your applications and automate payments.'
    }),
    position: 'bottom'
  },
  {
    selector: '.api-section[data-tour="settings-api-generate"]',
    content: t('tour.settings.api.generate', {
      ns: 'common',
      defaultValue: 'Generate new API keys for integrating with iSmartPay. Provide your email, password, and a description for the key.'
    }),
    position: 'bottom'
  },
  {
    selector: '.api-credentials-display[data-tour="settings-api-keys"], .api-section[data-tour="settings-api-generate"]',
    content: t('tour.settings.api.keys', {
      ns: 'common',
      defaultValue: 'View all your API keys here. Copy keys to use in your applications. Keep your API keys secure and never share them publicly.'
    }),
    position: 'top'
  }
]);

/**
 * Team Management Tour (Business-specific)
 */
export const getSettingsTeamTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.team.overview', {
      ns: 'common',
      defaultValue: 'Team Management - Create and manage teams, assign permissions, and invite team members. Control access to different pages and features for your team.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-team-overview"]',
    content: t('tour.settings.team.teams', {
      ns: 'common',
      defaultValue: 'View all your teams and manage team members. Create new teams or select existing teams to manage permissions.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-team-permissions"]',
    content: t('tour.settings.team.permissions', {
      ns: 'common',
      defaultValue: 'Control which pages each team can access. Enable or disable access to collections, disbursements, invoicing, and other sections.'
    }),
    position: 'bottom'
  },
  {
    selector: 'div[data-tour="settings-team-members"]',
    content: t('tour.settings.team.members', {
      ns: 'common',
      defaultValue: 'Manage team members for each team. Invite new members by email and remove members from teams as needed.'
    }),
    position: 'top'
  }
]);

/**
 * Main Settings Navigation Tour - Shows the tabs/navigation
 */
export const getSettingsTourSteps = (t) => filterExistingSteps([
  {
    selector: SETTINGS_NAV_SELECTOR,
    content: t('tour.settings.navigation', {
      ns: 'common',
      defaultValue: 'Navigate between different settings sections using these tabs. Each section helps you manage different aspects of your account.'
    }),
    position: 'bottom'
  }
]);

